/**
 * SOFSEK — Live Open-Transaction Store (§13). Tracks active, un-closed
 * transaction lifecycle tokens streamed from monitored external applications.
 * Closure is the external application's responsibility (D11) — this screen
 * also supports a manual, lookup-coded closure distinct from a source-system
 * one, and surfaces long-silent tokens against the per-application stale
 * threshold (sfk_application.stale_txn_threshold_days, NULL = never).
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { useAdaptiveList } from '../../lib/use_adaptive_list';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { getForensicActorId } from '../../types/auth';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { useTranslatorHydration, resolveNameForToken } from '../../lib/sfk_translator_hydrate';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.OPEN_TRANSACTION.LIST';
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface AppOpt { id: number; name: string; thresholdDays: number | null; }
interface AccountOpt { id: number; applicationId: number; label: string; }
interface PermOpt { id: number; applicationId: number; label: string; }

interface TxnRow {
  transaction_id: number;
  application_id: number;
  app_user_account_id: number;
  permission_id: number;
  entity_token: string;
  tracking_reference: string | null;
  transaction_status_code: string;
  opened_at: string;
  closed_at: string | null;
  transaction_closure_reason_code: string | null;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'application', header: 'Application', defaultWidth: 160 },
  { key: 'account', header: 'Master User Token', defaultWidth: 180 },
  { key: 'permission', header: 'Permission', defaultWidth: 220 },
  { key: 'entity_token', header: 'Target Entity', defaultWidth: 180 },
  { key: 'tracking_reference', header: 'Reference', defaultWidth: 140 },
  { key: 'opened_at', header: 'Opened', defaultWidth: 170 },
  { key: 'status', header: 'Status', defaultWidth: 160 },
];

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function TransactionInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [apps, setApps] = useState<AppOpt[]>([]);
  const [accounts, setAccounts] = useState<AccountOpt[]>([]);
  const [perms, setPerms] = useState<PermOpt[]>([]);
  const [statuses, setStatuses] = useState<CodeOpt[]>([]);
  const [closureReasons, setClosureReasons] = useState<CodeOpt[]>([]);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [appFacet, setAppFacet] = useState<string[]>([]);
  const [statusFacet, setStatusFacet] = useState<string[]>(['OPEN']);
  const [adding, setAdding] = useState(false);
  const [closingManual, setClosingManual] = useState<TxnRow | null>(null);

  // Lookups for name resolution + the add-modal pickers. NOTE: accounts/perms are
  // still loaded in bulk (capped by the row cap); resolving grid names beyond the
  // cap and the add-modal permission picker want the scoped-resolver / searchable
  // picker follow-up. The main list below is already cap-safe via the hook.
  const loadLookups = async () => {
    const [appRes, accRes, permRes, statusRes, reasonRes] = await Promise.all([
      apiDb.from('sfk_application').select('application_id, stale_txn_threshold_days, csm_application_master ( csm_application_master_tl ( locale_code, display_text ) )'),
      apiDb.from('sfk_app_user_account').select('app_user_account_id, application_id, external_username, csm_user_master!app_user_id ( display_name )'),
      apiDb.from('sfk_v_action').select('permission_id, application_id, display_name'),
      apiDb.from('sfk_transaction_status').select('transaction_status_code, sfk_transaction_status_tl ( locale_code, display_text )'),
      apiDb.from('sfk_transaction_closure_reason').select('transaction_closure_reason_code, sfk_transaction_closure_reason_tl ( locale_code, display_text )'),
    ]);
    const err = appRes.error ?? accRes.error ?? permRes.error ?? statusRes.error ?? reasonRes.error;
    if (err) { setLookupError(err.message); return; }
    setApps(((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ id: r.application_id as number, name: tlName((r.csm_application_master as { csm_application_master_tl?: { locale_code: string; display_text: string }[] } | null)?.csm_application_master_tl, String(r.application_id)), thresholdDays: r.stale_txn_threshold_days as number | null })));
    setAccounts(((accRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ id: r.app_user_account_id as number, applicationId: r.application_id as number, label: `${(r.csm_user_master as { display_name?: string } | null)?.display_name ?? ''} (${r.external_username as string})` })));
    setPerms(((permRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ id: r.permission_id as number, applicationId: r.application_id as number, label: r.display_name as string })));
    setStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.transaction_status_code as string, name: tlName(r.sfk_transaction_status_tl, r.transaction_status_code as string) })));
    setClosureReasons(((reasonRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.transaction_closure_reason_code as string, name: tlName(r.sfk_transaction_closure_reason_tl, r.transaction_closure_reason_code as string) })));
  };
  useEffect(() => { void loadLookups(); }, []);

  // Adaptive main list: instant client-side search under the cap, server-side above.
  const mapRow = useCallback((r: Record<string, unknown>) => r as unknown as TxnRow, []);
  const facets = useMemo(() => [
    { column: 'application_id', values: appFacet.map(Number) },
    { column: 'transaction_status_code', values: statusFacet },
  ], [appFacet, statusFacet]);
  const { rows, total, truncated, busy, error: listError, reload } =
    useAdaptiveList<Record<string, unknown>, TxnRow>({
      table: 'sfk_open_transaction',
      columns: 'transaction_id, application_id, app_user_account_id, permission_id, entity_token, tracking_reference, transaction_status_code, opened_at, closed_at, transaction_closure_reason_code',
      order: { column: 'opened_at', ascending: false },
      search: filter,
      searchColumns: ['entity_token', 'tracking_reference'],
      facets,
      map: mapRow,
    });
  const error = listError ?? lookupError;

  const appName = (id: number) => apps.find(a => a.id === id)?.name ?? String(id);
  const accountLabel = (id: number) => accounts.find(a => a.id === id)?.label ?? String(id);
  const permLabel = (id: number) => perms.find(p => p.id === id)?.label ?? String(id);
  const resolveNameForStatus = (code: string) => statuses.find(s => s.code === code)?.name ?? code;

  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  // §17.B13 — the resolved account/permission/application behind each open transaction.
  const buildRelatedGroups = (r: TxnRow): RelatedChildGroup[] => [
    { key: 'detail', title: t('sfk.open_txn.related.title', 'Transaction detail'), items: [
      { id: 'application', label: `${t('sfk.open_txn.col.application', 'Application')}: ${appName(r.application_id)}`, title: String(r.application_id) },
      { id: 'account', label: `${t('sfk.open_txn.col.account', 'Master User Token')}: ${accountLabel(r.app_user_account_id)}`, title: String(r.app_user_account_id) },
      { id: 'permission', label: `${t('sfk.open_txn.col.permission', 'Permission')}: ${permLabel(r.permission_id)}`, title: String(r.permission_id) },
    ] },
  ];

  const tokens = useMemo(() => rows.flatMap(r => [r.entity_token, r.tracking_reference].filter((v): v is string => !!v)), [rows]);
  const hydrated = useTranslatorHydration(tokens);

  const isStale = (r: TxnRow): boolean => {
    if (r.transaction_status_code !== 'OPEN') return false;
    const threshold = apps.find(a => a.id === r.application_id)?.thresholdDays;
    if (!threshold) return false;
    const openedMs = new Date(r.opened_at).getTime();
    return Date.now() - openedMs > threshold * 86400000;
  };

  const closeSource = async (row: TxnRow) => {
    setLookupError(null);
    try {
      const { error: e } = await routedUpdate(session, 'sfk_open_transaction', 'transaction_id', row.transaction_id, {
        transaction_status_code: 'CLOSED_SOURCE',
        closed_at: new Date().toISOString(),
      });
      if (e) throw new Error(e.message);
      reload();
    } catch (e) { setLookupError((e as Error).message); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.open_txn.title', 'Live Open-Transaction Store')}
          description={t('sfk.open_txn.description', 'Active, un-closed transaction lifecycle tokens streamed from monitored applications.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setAdding(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('sfk.open_txn.new', 'New Transaction')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: total, label: t('sfk.open_txn.stat.total', 'Transactions') },
          { value: rows.length, label: t('sfk.open_txn.stat.shown', 'Shown') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.open_txn.facet.app', 'Application')} options={apps.map(a => ({ value: String(a.id), label: a.name }))} selected={appFacet} onChange={setAppFacet} />
            <FacetFilter label={t('sfk.open_txn.facet.status', 'Status')} options={statuses.map(s => ({ value: s.code, label: s.name }))} selected={statusFacet} onChange={setStatusFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || appFacet.length > 0 || statusFacet.length !== 1} onClear={() => { setFilter(''); setAppFacet([]); setStatusFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {truncated && !busy && (
                <span className="text-csm-xs text-csm-muted">{t('common.list.truncated', 'Showing first {{n}} of {{total}} — refine your search', { n: rows.length, total })}</span>
              )}
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <Fragment key={r.transaction_id}>
                  <tr className={`hover:bg-csm-surface-alt ${isStale(r) ? 'bg-csm-danger-soft' : ''}`}>
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle">
                      <RelatedExpandButton open={detail.isOpen(r.transaction_id)} hasChildren onToggle={() => detail.toggle(r.transaction_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'application' && appName(r.application_id)}
                        {col.key === 'account' && accountLabel(r.app_user_account_id)}
                        {col.key === 'permission' && permLabel(r.permission_id)}
                        {col.key === 'entity_token' && resolveNameForToken(hydrated, r.entity_token)}
                        {col.key === 'tracking_reference' && (r.tracking_reference ? resolveNameForToken(hydrated, r.tracking_reference) : '—')}
                        {col.key === 'opened_at' && formatDateTime(r.opened_at)}
                        {col.key === 'status' && (
                          <span>
                            {resolveNameForStatus(r.transaction_status_code)}
                            {isStale(r) && <span className="ml-1 rounded bg-csm-danger px-1 text-csm-xs text-white">{t('sfk.open_txn.stale', 'Stale')}</span>}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {r.transaction_status_code === 'OPEN' && (
                        <>
                          {canChange && (<button onClick={() => void closeSource(r)} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.open_txn.close_source', 'Close (Source)')}</button>)}
                          {canChange && (<button onClick={() => setClosingManual(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.open_txn.close_manual', 'Close (Manual)')}</button>)}
                        </>
                      )}
                    </td>
                  </tr>
                  {detail.isOpen(r.transaction_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {rows.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {adding && (
        <AddTransactionModal session={session} apps={apps} accounts={accounts} perms={perms}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); void reload(); }} />
      )}
      {closingManual && (
        <CloseManualModal session={session} row={closingManual} reasons={closureReasons}
          onClose={() => setClosingManual(null)}
          onSaved={() => { setClosingManual(null); void reload(); }} />
      )}
    </div>
  );
}

function AddTransactionModal({ session, apps, accounts, perms, onClose, onSaved }: {
  session: AuthSession; apps: AppOpt[]; accounts: AccountOpt[]; perms: PermOpt[];
  onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState<number | ''>(apps[0]?.id ?? '');
  const [accountId, setAccountId] = useState<number | ''>('');
  const [permissionId, setPermissionId] = useState<number | ''>('');
  const [entityToken, setEntityToken] = useState('');
  const [trackingReference, setTrackingReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopedAccounts = accounts.filter(a => a.applicationId === applicationId);
  const scopedPerms = perms.filter(p => p.applicationId === applicationId);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!applicationId || !accountId || !permissionId || !entityToken.trim())
        throw new Error(t('sfk.open_txn.err.required', 'Application, account, permission, and entity token are required.'));
      const { error: e } = await routedInsert(session, 'sfk_open_transaction', {
        application_id: applicationId,
        app_user_account_id: accountId,
        permission_id: permissionId,
        entity_token: entityToken.trim(),
        tracking_reference: trackingReference.trim() || null,
        modified_by: getForensicActorId(session),
      });
      if (e) throw new Error(e.message);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.open_txn.new_title', 'New Open Transaction')}
      onClose={onClose}
      width="sm"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="application_id" label={t('sfk.open_txn.application', 'Application')}>
        <label className={labelCls}>{t('sfk.open_txn.application', 'Application')}</label>
        <select value={applicationId} onChange={e => { setApplicationId(Number(e.target.value)); setAccountId(''); setPermissionId(''); }} className={`${inputCls} bg-white`}>
          {apps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="app_user_account_id" label={t('sfk.open_txn.account', 'Master User Token (Account)')}>
        <label className={labelCls}>{t('sfk.open_txn.account', 'Master User Token (Account)')}</label>
        <select value={accountId} onChange={e => setAccountId(e.target.value ? Number(e.target.value) : '')} className={`${inputCls} bg-white`}>
          <option value="">{t('common.select', 'Select…')}</option>
          {scopedAccounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="permission_id" label={t('sfk.open_txn.permission', 'Permission Signature')}>
        <label className={labelCls}>{t('sfk.open_txn.permission', 'Permission Signature')}</label>
        <select value={permissionId} onChange={e => setPermissionId(e.target.value ? Number(e.target.value) : '')} className={`${inputCls} bg-white`}>
          <option value="">{t('common.select', 'Select…')}</option>
          {scopedPerms.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="entity_token" label={t('sfk.open_txn.entity_token', 'Target Entity ID')}>
        <label className={labelCls}>{t('sfk.open_txn.entity_token', 'Target Entity ID')}</label>
        <input value={entityToken} onChange={e => setEntityToken(e.target.value)} className={inputCls} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="tracking_reference" label={t('sfk.open_txn.tracking_reference', 'Tracking Reference')}>
        <label className={labelCls}>{t('sfk.open_txn.tracking_reference', 'Tracking Reference')}</label>
        <input value={trackingReference} onChange={e => setTrackingReference(e.target.value)} className={inputCls} />
      </PersonalizedField>
      {error && <div className="mt-1 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

function CloseManualModal({ session, row, reasons, onClose, onSaved }: {
  session: AuthSession; row: TxnRow; reasons: CodeOpt[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState(reasons[0]?.code ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!reason) throw new Error(t('sfk.open_txn.err.reason_required', 'A closure reason is required.'));
      const { error: e } = await routedUpdate(session, 'sfk_open_transaction', 'transaction_id', row.transaction_id, {
        transaction_status_code: 'CLOSED_MANUAL',
        closed_at: new Date().toISOString(),
        transaction_closure_reason_code: reason,
      });
      if (e) throw new Error(e.message);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.open_txn.close_manual_title', 'Close Transaction Manually')}
      onClose={onClose}
      width="sm"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="transaction_closure_reason_code" label={t('sfk.open_txn.closure_reason', 'Closure Reason')}>
        <label className={labelCls}>{t('sfk.open_txn.closure_reason', 'Closure Reason')}</label>
        <select value={reason} onChange={e => setReason(e.target.value)} className={`${inputCls} bg-white`}>
          {reasons.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
        </select>
      </PersonalizedField>
      {error && <div className="mt-1 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkOpenTransactionPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <TransactionInner session={session} />
    </PersonalizationProvider>
  );
}
